import React from 'react';
import { Form, Switch, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { T, H_TITLE, H_SUB, SECTION_LABEL } from './declarationTheme';
import { Field, Grid2, Input, DatePicker, Select, inputProps, selectProps } from './fields';
import { CATEGORIES_PERMIS } from '../../../variables/constants';

const renderCategorieTag = ({ value, closable, onClose }) => (
  <Tag
    closable={closable}
    onClose={onClose}
    style={{
      marginInlineEnd: 4,
      padding: '0 8px',
      height: 22,
      lineHeight: '20px',
      fontFamily: T.fontMono,
      fontWeight: 600,
      fontSize: 12,
      borderRadius: 4,
      background: T.brandSoft,
      borderColor: T.brandLine,
      color: T.brand,
    }}
  >
    {value}
  </Tag>
);

/* ─────────────────────────────────────────────────────────────
   Étape 4 - Conducteur au moment du sinistre
   - Toggle "L'assuré est le conducteur" (par défaut Oui)
   - Si Oui : on ne demande que les infos permis
   - Si Non : nom, prénom, date naissance + permis
   ───────────────────────────────────────────────────────────── */
export default function StepConducteur({ form, embedded = false }) {
  const { t } = useTranslation('declaration');
  const estAssure = Form.useWatch('conducteurEstAssure', form) ?? true;

  const Wrapper = embedded ? React.Fragment : 'div';
  const wrapperProps = embedded ? {} : { style: { maxWidth: 760 } };

  return (
    <Wrapper {...wrapperProps}>
      {!embedded && (
        <>
          <h1 style={H_TITLE}>{t('conducteur.titre')}</h1>
          <p style={H_SUB}>
            {t('conducteur.sousTitre')}
          </p>
        </>
      )}

      {/* ══════ Toggle assuré / autre ══════ */}
      <div
        style={{
          marginTop: embedded ? 0 : 36,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 16px', border: `1px solid ${T.line}`,
          borderRadius: T.r.md,
        }}
      >
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.inkSoft }}>
            {t('conducteur.assureEstConducteur')}
          </div>
          <div style={{ fontSize: 11.5, color: T.warn, marginTop: 2 }}>
            {t('conducteur.assureEstConducteurAide')}
          </div>
        </div>
        <Form.Item name="conducteurEstAssure" valuePropName="checked" noStyle initialValue>
          <Switch
            checkedChildren={t('commun.oui')}
            unCheckedChildren={t('commun.non')}
            style={{ background: estAssure ? T.brand : undefined }}
          />
        </Form.Item>
      </div>

      {/* ══════ Identité conducteur - uniquement si ≠ assuré ══════ */}
      {!estAssure && (
        <div style={{ marginTop: 24 }}>
          <div style={SECTION_LABEL}>{t('conducteur.identite')}</div>

          <Grid2>
            <Field
              name="conducteurNom"
              label={t('conducteur.nom')}
              rules={[{ required: !estAssure, message: t('conducteur.nomRequis') }]}
            >
              <Input {...inputProps} placeholder="BABOZA PETEMBADA" maxLength={200} />
            </Field>
            <Field
              name="conducteurPrenom"
              label={t('conducteur.prenom')}
              rules={[{ required: !estAssure, message: t('conducteur.prenomRequis') }]}
            >
              <Input {...inputProps} placeholder="Amos" maxLength={200} />
            </Field>
          </Grid2>

          <Grid2>
            <Field
              name="conducteurDateNaissance"
              label={t('conducteur.dateNaissance')}
              rules={[{ required: !estAssure, message: t('conducteur.dateRequise') }]}
            >
              <DatePicker
                style={{ ...inputProps.style, width: '100%' }}
                format="DD/MM/YYYY"
                placeholder={t('commun.formatDate')}
                disabledDate={(d) => d && d.isAfter(dayjs(), 'day')}
              />
            </Field>
            <div />
          </Grid2>
        </div>
      )}

      {/* ══════ Permis ══════ */}
      <div style={{ marginTop: estAssure ? 24 : 20 }}>
        <div style={SECTION_LABEL}>{t('conducteur.informationsPermis')}</div>

        <Grid2>
          <Field name="conducteurNumeroPermis" label={t('conducteur.numeroPermis')}>
            <Input
              {...inputProps}
              placeholder={t('conducteur.numeroPermisExemple')}
              maxLength={30}
              style={{ ...inputProps.style, fontFamily: T.fontMono, letterSpacing: '0.02em' }}
            />
          </Field>

          <Field name="conducteurCategoriesPermis" label={t('conducteur.categories')}>
            <Select
              {...selectProps}
              style={{ ...selectProps.style, height: 'auto', minHeight: 40 }}
              mode="multiple"
              placeholder="A, B, C, D…"
              optionFilterProp="label"
              maxTagCount="responsive"
              tagRender={renderCategorieTag}
              options={CATEGORIES_PERMIS.map(c => ({ label: c.label, value: c.value }))}
            />
          </Field>
        </Grid2>

        <Grid2>
          <Field name="conducteurDateDelivrance" label={t('conducteur.dateDelivrance')}>
            <DatePicker
              style={{ ...inputProps.style, width: '100%' }}
              format="DD/MM/YYYY"
              placeholder={t('commun.formatDate')}
              disabledDate={(d) => d && d.isAfter(dayjs(), 'day')}
            />
          </Field>
          <Field name="conducteurLieuDelivrance" label={t('conducteur.lieuDelivrance')}>
            <Input {...inputProps} placeholder={t('conducteur.lieuDelivranceExemple')} maxLength={100} />
          </Field>
        </Grid2>
      </div>
    </Wrapper>
  );
}
